import React, { useState, useEffect } from 'react';
import BackButton from '@/components/BackButton';
import EmployeeAvatar from '@/components/EmployeeAvatar';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { UserPlus, PencilSimple, Trash, MagnifyingGlass, SpinnerGap, Eye, EyeSlash, Calculator, Key, FileXls, Paperclip, ArrowsClockwise, User, ClockClockwise, MapPin, Megaphone, Certificate } from '@phosphor-icons/react';
import { Sar } from '@/components/SarIcon';
import { formatRiyadh } from '@/lib/time';
import { DEPARTMENTS, getDeptLabel, getSubDeptLabel } from '@/constants/departments';
import useDepartments from '@/hooks/useDepartments';
import { NATIONALITIES } from '@/constants/nationalities';
import BasicTab from './employees/tabs/BasicTab';
import ContactTab from './employees/tabs/ContactTab';
import IdentityTab from './employees/tabs/IdentityTab';
import ContractTab from './employees/tabs/ContractTab';
import SalaryTab from './employees/tabs/SalaryTab';
import ScheduleTab from './employees/tabs/ScheduleTab';
import ImportEmployeesDialog from './employees/ImportEmployeesDialog';
import EmployeeDocumentsDialog from './employees/EmployeeDocumentsDialog';
import YearlyHistoryTab from './employees/tabs/YearlyHistoryTab';
import LucentLoader from '@/components/LucentLoader';
import useEmployeeBadges from '@/hooks/useEmployeeBadges';
import ScrollShadow from '@/components/ScrollShadow';

// employment_type → small badge meta
const EMP_TYPE_BADGE = {
  remote:            { ar: 'عن بُعد',       en: 'Remote',       cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  flexible:          { ar: 'دوام مرن',      en: 'Flexible',     cls: 'bg-sky-100 text-sky-800 border border-sky-200' },
  consultant:        { ar: 'مستشار',        en: 'Consultant',   cls: 'bg-rose-100 text-rose-800 border border-rose-200' },
  attendance_based:  { ar: 'حضور فقط',      en: 'Per-day',      cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
  part_time:         { ar: 'دوام جزئي',     en: 'Part-time',    cls: 'bg-indigo-100 text-indigo-800 border border-indigo-200' },
};

const EmploymentTypeBadge = ({ type, language }) => {
  const meta = EMP_TYPE_BADGE[type];
  if (!meta) return null;
  return (
    <span className={`ms-2 text-[10px] px-1.5 py-0.5 rounded-full ${meta.cls}`}
          data-testid={`emp-type-badge-${type}`}>
      {language === 'ar' ? meta.ar : meta.en}
    </span>
  );
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

const defaultForm = {
  name: '', name_en: '', email: '', password: '', department: '', sub_department: '', position: '', position_en: '', phone: '',
  hire_date: '', birth_date: '', iqama_number: '', id_expiry_date: '', employee_number: '', nationality: '',
  contract_start_date: '', contract_end_date: '', qiwa_status: '',
  basic_salary: '', housing_allowance: '', transport_allowance: '', other_allowances: '',
  gosi_subscription: '',
  notification_language: 'ar',
  is_department_manager: false,
  is_finance_manager: false,
  is_general_manager: false,
  is_government_officer: false,
  manager_id: '',
  annual_leave_days: 30,
  exclude_from_overtime: false,
  is_practitioner: false,
  employment_type: 'on_site',
  employment_type_reason: '',
  use_custom_schedule: false,
  custom_schedule: { daily_hours: 8, start_time: '08:00', end_time: '16:00', rest_days: [4], grace_minutes: 30, late_penalty_enabled: true },
  is_contracted_worker: false,
  is_doctor: false,
  doctor_type: '',  // 'salary' | 'percentage'
  manual_attendance_allowed: false,
  allow_multiple_punches: false,
};

const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const special = '@#$!';
  let pass = '';
  for (let i = 0; i < 8; i++) pass += chars.charAt(Math.floor(Math.random() * chars.length));
  pass += special.charAt(Math.floor(Math.random() * special.length));
  return pass;
};

const isArabic = (text) => /^[\u0600-\u06FF\s\d.,]+$/.test(text);
const isEnglish = (text) => /^[a-zA-Z\s\d.,@_\-]+$/.test(text);

export default function EmployeesPage() {
  const { t } = useTranslation();
  const { direction, language } = useLanguage();
  const { user: currentUser } = useAuth();
  // "مدير النظام" (system admin) — only they can zoom into employee photos.
  const isSysAdmin = currentUser?.role === 'admin' || currentUser?.role === 'sys_admin';
  const navigate = useNavigate();
  // Refresh the module-scope badges cache after create/update so
  // classification badges (أجير / مرن / طبيب…) appear immediately on
  // Payroll / Attendance / Qiwa / Leaves without a hard page reload.
  const { refresh: refreshBadges } = useEmployeeBadges();
  const [departments] = useDepartments();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [nationalityFilter, setNationalityFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // HR Summons — one-modal call-in feature.
  const [summonTarget, setSummonTarget] = useState(null);
  // v1.5.45 — Training certificate dialog state.
  const [certTarget, setCertTarget] = useState(null);
  const [certType, setCertType] = useState('quality_patient_safety');
  const [certGrantDate, setCertGrantDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [certIssuing, setCertIssuing] = useState(false);
  // v1.5.50 — Bulk one-shot certificate issuance (all 3 for all eligible staff).
  const [bulkCertsBusy, setBulkCertsBusy] = useState(false);
  const [summonAppt, setSummonAppt] = useState('');
  const [summonMsg, setSummonMsg] = useState('');
  const [summonSending, setSummonSending] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEosDialog, setShowEosDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [formData, setFormData] = useState({ ...defaultForm });
  const [saving, setSaving] = useState(false);
  const [eosData, setEosData] = useState(null);
  const [eosType, setEosType] = useState('termination');
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [activeTab, setActiveTab] = useState('basic');
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [employmentTypeFilter, setEmploymentTypeFilter] = useState('');
  const [showDocsDialog, setShowDocsDialog] = useState(false);
  // Salaries are masked (blurred) by default. Admin clicks the eye icon
  // next to "Total Salary" to reveal them.
  const [revealSalary, setRevealSalary] = useState(false);
  // Two-mode delete: data entry error (hard) vs contract termination (soft).
  const [deleteReason, setDeleteReason] = useState('data_entry_error');
  const [terminationNote, setTerminationNote] = useState('');
  const [terminationDate, setTerminationDate] = useState(new Date().toISOString().slice(0, 10));
  // Roster vs terminated employees toggle.
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'terminated'
  const [terminatedList, setTerminatedList] = useState([]);
  // Login sessions admin view (per employee).
  const [showLoginSessionsDialog, setShowLoginSessionsDialog] = useState(false);
  const [loginSessions, setLoginSessions] = useState([]);
  const [loginSessionsEmp, setLoginSessionsEmp] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  // Map of user_id → overage_days (days the employee has consumed beyond entitlement).
  // Drives the red "رصيد سالب" badge next to each employee's name.
  const [overageByUserId, setOverageByUserId] = useState({});

  useEffect(() => { fetchEmployees(); fetchOverages(); }, []);

  const validateForm = (isEdit = false) => {
    const errors = {};
    const missing = [];
    if (!formData.name) { errors.name = true; missing.push(t('employee_name')); }
    if (!formData.name_en) { errors.name_en = true; missing.push(t('employee_name_en')); }
    if (!isEdit && !formData.email) { errors.email = true; missing.push(t('email')); }
    if (!isEdit && !formData.password) { errors.password = true; missing.push(t('password')); }
    if (!formData.department) { errors.department = true; missing.push(t('department')); }
    if (!formData.position) { errors.position = true; missing.push(t('position')); }
    if (!formData.phone) { errors.phone = true; missing.push(t('whatsapp_number')); }
    if (!formData.iqama_number) { errors.iqama_number = true; missing.push(t('iqama_number')); }
    if (!formData.employee_number) { errors.employee_number = true; missing.push(t('employee_number')); }
    if (!formData.nationality) { errors.nationality = true; missing.push(t('nationality')); }
    if (!formData.hire_date) { errors.hire_date = true; missing.push(t('hire_date')); }
    if (!formData.contract_start_date) { errors.contract_start_date = true; missing.push(t('contract_start_date')); }
    if (!formData.contract_end_date) { errors.contract_end_date = true; missing.push(t('contract_end_date')); }
    const hasSalary = formData.basic_salary || formData.housing_allowance || formData.transport_allowance || formData.other_allowances;
    if (!hasSalary) { errors.salary = true; missing.push(t('salary_required')); }
    setFormErrors(errors);
    if (missing.length > 0) {
      toast.error(t('fill_required'), { description: missing.slice(0, 3).join(' - ') + (missing.length > 3 ? ` (+${missing.length - 3})` : '') });
    }
    return Object.keys(errors).length === 0;
  };

  const fetchEmployees = async () => {
    try {
      const { data } = await axios.get(`${API}/employees`, { withCredentials: true });
      // Always sort by employee_number ASC. Treat the field as numeric when
      // possible so "9" comes before "10". Fallback to lexicographic compare
      // for non-numeric IDs (mixed legacy data). Employees without a number
      // sink to the bottom.
      const sorted = [...(data || [])].sort((a, b) => {
        const av = (a?.employee_number || '').toString().trim();
        const bv = (b?.employee_number || '').toString().trim();
        if (!av && !bv) return 0;
        if (!av) return 1;
        if (!bv) return -1;
        const an = Number(av);
        const bn = Number(bv);
        if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
        return av.localeCompare(bv, 'ar', { numeric: true });
      });
      setEmployees(sorted);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverages = async () => {
    try {
      // Lightweight endpoint — bulk-aggregated, independent of employee count.
      const { data } = await axios.get(`${API}/leaves/admin/overages-lite`, { withCredentials: true });
      const map = {};
      (data || []).forEach((r) => {
        if ((r.overage_days || 0) > 0) map[r.user_id] = r.overage_days;
      });
      setOverageByUserId(map);
    } catch (e) {
      // silent — badge is informational, not blocking
    }
  };

  // v1.5.50 — One-shot bulk issuance for HR migration.
  const handleBulkIssueCertificates = async () => {
    const isAr = language === 'ar';
    const confirmMsg = isAr
      ? 'سيتم توليد 3 شهادات تدريب لكل موظف مباشِر قبل 30/07/2026 بتاريخ عشوائي وتنزيلها لك كملف ZIP. لن يتم إرسال أي إيميل أو إشعار للموظفين. هل تريد المتابعة؟'
      : 'This will generate 3 training certificates (random dates) for every employee hired on/before 30/07/2026 and download them to your device as a ZIP. No emails or notifications will be sent to employees. Continue?';
    if (!window.confirm(confirmMsg)) return;
    setBulkCertsBusy(true);
    try {
      const res = await axios.post(
        `${API}/certificates/bulk-issue-all`,
        {},
        { withCredentials: true, responseType: 'blob' }
      );
      // Trigger ZIP download
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lucent_training_certificates_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const issued = res.headers['x-bulk-issued'] || '?';
      const emps = res.headers['x-bulk-employees'] || '?';
      const skipped = res.headers['x-bulk-skipped'] || '0';
      toast.success(
        isAr
          ? `تم تنزيل ${issued} شهادة لعدد ${emps} موظف — تم استثناء ${skipped}`
          : `Downloaded ${issued} certificates for ${emps} employees — ${skipped} skipped`,
        { duration: 6000 }
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail
        || (isAr ? 'تعذّر تنزيل الشهادات' : 'Certificate download failed')
      );
    } finally {
      setBulkCertsBusy(false);
    }
  };


  const handleAdd = async () => {
    if (!validateForm(false)) return;
    setSaving(true);
    try {
      const phoneNum = formData.phone ? `+966${formData.phone.replace(/^0+/, '')}` : null;
      const pwd = formData.password;
      const payload = {
        ...formData,
        phone: phoneNum,
        employment_type: formData.employment_type || 'on_site',
        employment_type_reason: formData.employment_type_reason || '',
        basic_salary: formData.basic_salary ? parseFloat(formData.basic_salary) : null,
        housing_allowance: formData.housing_allowance ? parseFloat(formData.housing_allowance) : null,
        transport_allowance: formData.transport_allowance ? parseFloat(formData.transport_allowance) : null,
        other_allowances: formData.other_allowances ? parseFloat(formData.other_allowances) : null,
        // GOSI is a fixed manual amount, only stored for Saudi nationals. For
        // non-Saudis we force it to 0 so it doesn't leak from a previous edit.
        gosi_subscription: formData.nationality === 'سعودي'
          ? (formData.gosi_subscription ? parseFloat(formData.gosi_subscription) : 0)
          : 0,
        // Only persist the custom_schedule when the admin explicitly enabled
        // it. Otherwise we send null so the schedule resolution falls back
        // to the department schedule (and not to the form's defaults).
        custom_schedule: formData.use_custom_schedule ? formData.custom_schedule : null,
        // Normalize doctor fields — only send `doctor_type` when is_doctor is true.
        is_doctor: !!formData.is_doctor,
        doctor_type: formData.is_doctor ? (formData.doctor_type || null) : null,
      };
      // Strip the UI-only toggle so the backend never sees it.
      delete payload.use_custom_schedule;
      const { data } = await axios.post(`${API}/employees`, payload, { withCredentials: true });

      // Download the welcome letter PDF locally, but explicitly tell the
      // backend NOT to send an email (send_email=false). The welcome email
      // will only be dispatched later when the admin clicks
      // "Reset password & print letter" on the employee row.
      if (data.id) {
        try {
          const res = await axios.get(
            `${API}/employees/${data.id}/welcome-letter?password=${encodeURIComponent(pwd)}&send_email=false`,
            { withCredentials: true, responseType: 'blob' },
          );
          const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
          const link = document.createElement('a');
          link.href = url;
          link.download = `welcome_letter_${formData.name_en || formData.name}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (e) {
          console.error('Failed to download welcome letter:', e);
        }
      }

      // Two-step Add flow:
      // After the employee is created, keep the dialog OPEN and switch to
      // the "Salary & Balance" tab so the admin can enter legacy yearly
      // history (now that an employeeId exists). Refresh the list in
      // background. The dialog will close when the admin clicks "إنهاء"
      // on the history tab.
      toast.success(t('success'));
      setSelectedEmployee({ ...data, id: data.id });
      setActiveTab('history');
      fetchEmployees();
      refreshBadges();
    } catch (error) {
      if (error.response?.data?.detail === 'Employee number already exists') {
        setFormErrors(prev => ({ ...prev, employee_number: true }));
        setActiveTab('identity');
        toast.error(t('employee_number_exists'));
      } else if (error.response?.data?.detail === 'Email already registered') {
        setFormErrors(prev => ({ ...prev, email: true }));
        setActiveTab('basic');
        toast.error(t('email_exists'));
      } else {
        toast.error(t('error'));
      }
      console.error('Failed to add employee:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    if (!validateForm(true)) return;
    setSaving(true);
    try {
      const phoneNum = formData.phone ? `+966${formData.phone.replace(/^0+/, '')}` : null;
      const payload = {
        name: formData.name, name_en: formData.name_en,
        department: formData.department, sub_department: formData.sub_department,
        position: formData.position, position_en: formData.position_en, phone: phoneNum,
        hire_date: formData.hire_date,
        birth_date: formData.birth_date,
        iqama_number: formData.iqama_number, id_expiry_date: formData.id_expiry_date,
        employee_number: formData.employee_number,
        nationality: formData.nationality,
        contract_start_date: formData.contract_start_date, contract_end_date: formData.contract_end_date,
        qiwa_status: formData.qiwa_status,
        basic_salary: formData.basic_salary ? parseFloat(formData.basic_salary) : null,
        housing_allowance: formData.housing_allowance ? parseFloat(formData.housing_allowance) : null,
        transport_allowance: formData.transport_allowance ? parseFloat(formData.transport_allowance) : null,
        other_allowances: formData.other_allowances ? parseFloat(formData.other_allowances) : null,
        gosi_subscription: formData.nationality === 'سعودي'
          ? (formData.gosi_subscription ? parseFloat(formData.gosi_subscription) : 0)
          : 0,
        is_department_manager: formData.is_department_manager,
        is_finance_manager: !!formData.is_finance_manager,
        is_general_manager: !!formData.is_general_manager,
        // v1.5.42 — Scoped Government Affairs delegation. Persisted
        // via the same payload as the other permission flags so the
        // save button on the employee form doesn't drop it.
        is_government_officer: !!formData.is_government_officer,
        manager_id: formData.manager_id || null,
        annual_leave_days: parseInt(formData.annual_leave_days, 10) || 30,
        exclude_from_overtime: !!formData.exclude_from_overtime,
        is_practitioner: !!formData.is_practitioner,
        employment_type: formData.employment_type || 'on_site',
        employment_type_reason: formData.employment_type_reason || '',
        custom_schedule: formData.use_custom_schedule ? formData.custom_schedule : null,
        is_contracted_worker: !!formData.is_contracted_worker,
        is_doctor: !!formData.is_doctor,
        doctor_type: formData.is_doctor ? (formData.doctor_type || null) : null,
        manual_attendance_allowed: !!formData.manual_attendance_allowed,
        allow_multiple_punches: !!formData.allow_multiple_punches,
      };
      const cleaned = Object.fromEntries(Object.entries(payload).filter(([k, v]) => {
        if (k === 'use_custom_schedule') return false;  // UI-only toggle
        if (k === 'is_department_manager') return true;
        if (k === 'is_finance_manager') return true;
        if (k === 'is_general_manager') return true;
        if (k === 'is_government_officer') return true;
        if (k === 'manager_id') return true;  // null is meaningful (clears the manager)
        if (k === 'annual_leave_days') return true;
        if (k === 'exclude_from_overtime') return true;
        if (k === 'is_practitioner') return true;
        if (k === 'employment_type') return true;
        if (k === 'employment_type_reason') return true;
        if (k === 'custom_schedule') return true;
        if (k === 'is_contracted_worker') return true;
        if (k === 'is_doctor') return true;
        if (k === 'doctor_type') return true;  // null clears the type
        if (k === 'manual_attendance_allowed') return true;
        if (k === 'allow_multiple_punches') return true;
        return v !== '' && v !== null && v !== undefined;
      }));
      await axios.put(`${API}/employees/${selectedEmployee.id}`, cleaned, { withCredentials: true });
      setShowEditDialog(false);
      setFormData({ ...defaultForm });
      setActiveTab('basic');
      toast.success(t('success'));
      fetchEmployees();
      refreshBadges();
    } catch (error) {
      if (error.response?.data?.detail === 'Employee number already exists') {
        setFormErrors(prev => ({ ...prev, employee_number: true }));
        setActiveTab('identity');
        toast.error(t('employee_number_exists'));
      } else {
        toast.error(t('error'));
      }
      console.error('Failed to update employee:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const payload = {
        reason: deleteReason,
        termination_reason_text: deleteReason === 'contract_termination' ? terminationNote : undefined,
        termination_date: deleteReason === 'contract_termination' ? terminationDate : undefined,
      };
      await axios.delete(`${API}/employees/${selectedEmployee.id}`, {
        withCredentials: true,
        data: payload,
      });
      setShowDeleteDialog(false);
      setSelectedEmployee(null);
      setDeleteReason('data_entry_error');
      setTerminationNote('');
      setTerminationDate(new Date().toISOString().slice(0, 10));
      toast.success(
        deleteReason === 'contract_termination'
          ? (language === 'ar' ? 'تم نقل الموظف إلى قائمة المُنهَى تعاقدهم' : 'Employee moved to Terminated list')
          : t('success')
      );
      fetchEmployees();
      if (viewMode === 'terminated') fetchTerminated();
    } catch (error) {
      console.error('Failed to delete employee:', error);
      toast.error(error?.response?.data?.detail || t('error'));
    }
  };

  // Terminated-employees roster (read-only). Loaded lazily on tab switch.
  const fetchTerminated = async () => {
    try {
      const { data } = await axios.get(`${API}/employees/terminated`, { withCredentials: true });
      setTerminatedList(data || []);
    } catch (e) {
      console.error('Failed to fetch terminated:', e);
    }
  };

  const handleReinstate = async (emp) => {
    if (!window.confirm(language === 'ar'
      ? `هل أنت متأكد من إعادة تفعيل ${emp.name}؟`
      : `Reinstate ${emp.name}?`)) return;
    try {
      await axios.put(`${API}/employees/${emp.id}/reinstate`, {}, { withCredentials: true });
      toast.success(language === 'ar' ? 'تمت إعادة التفعيل' : 'Reinstated');
      fetchTerminated();
      fetchEmployees();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('error'));
    }
  };

  // Show recent login attempts for a given employee (success + failed).
  const openLoginSessions = async (emp) => {
    setLoginSessionsEmp(emp);
    setShowLoginSessionsDialog(true);
    setLoginSessions([]);
    setLoadingSessions(true);
    try {
      const { data } = await axios.get(
        `${API}/auth/login-sessions/${emp.id}?limit=100`,
        { withCredentials: true },
      );
      setLoginSessions(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('error'));
    } finally {
      setLoadingSessions(false);
    }
  };

  const openEditDialog = (emp) => {
    setSelectedEmployee(emp);
    const deptMatch = departments.find(d => d.value === emp.department);
    const subMatch = deptMatch?.subs?.find(s => s.value === emp.sub_department);
    setFormData({
      name: emp.name || '', name_en: emp.name_en || '', email: emp.email || '', password: '',
      department: emp.department || '', sub_department: emp.sub_department || '', position: emp.position || '', position_en: emp.position_en || '', phone: emp.phone?.replace('+966', '') || '',
      hire_date: emp.hire_date || '',
      birth_date: emp.birth_date || '',
      iqama_number: emp.iqama_number || '', id_expiry_date: emp.id_expiry_date || '',
      employee_number: emp.employee_number || '',
      nationality: emp.nationality || '',
      contract_start_date: emp.contract_start_date || '', contract_end_date: emp.contract_end_date || '',
      qiwa_status: emp.qiwa_status || '',
      basic_salary: emp.basic_salary?.toString() || '', housing_allowance: emp.housing_allowance?.toString() || '',
      transport_allowance: emp.transport_allowance?.toString() || '', other_allowances: emp.other_allowances?.toString() || '',
      gosi_subscription: emp.gosi_subscription?.toString() || '',
      is_department_manager: emp.is_department_manager || false,
      is_finance_manager: emp.is_finance_manager || false,
      is_general_manager: emp.is_general_manager || emp.role === 'general_manager' || false,
      is_government_officer: emp.is_government_officer || false,
      manager_id: emp.manager_id || '',
      annual_leave_days: emp.annual_leave_days || 30,
      exclude_from_overtime: emp.exclude_from_overtime || false,
      is_practitioner: emp.is_practitioner || false,
      is_contracted_worker: emp.is_contracted_worker || false,
      is_doctor: emp.is_doctor || false,
      doctor_type: emp.doctor_type || '',
      manual_attendance_allowed: emp.manual_attendance_allowed || false,
      allow_multiple_punches: emp.allow_multiple_punches || false,
      employment_type: emp.employment_type || 'on_site',
      employment_type_reason: emp.employment_type_reason || '',
      use_custom_schedule: !!emp.custom_schedule,
      custom_schedule: emp.custom_schedule || { daily_hours: 8, start_time: '08:00', end_time: '16:00', rest_days: [4], grace_minutes: 30, late_penalty_enabled: true },
    });
    setShowEditDialog(true);
    setFormErrors({});
    setActiveTab('basic');
  };

  const openDetailDialog = async (emp) => {
    setSelectedEmployee(emp);
    setShowDetailDialog(true);
    try {
      const [eosRes, leaveRes] = await Promise.all([
        axios.get(`${API}/employees/${emp.id}/eos?termination_type=termination`, { withCredentials: true }),
        axios.get(`${API}/employees/${emp.id}/leave-balance`, { withCredentials: true })
      ]);
      setEosData(eosRes.data);
      setLeaveBalance(leaveRes.data);
    } catch (e) {
      console.error('Failed to fetch details:', e);
    }
  };

  const openEosDialog = async (emp) => {
    setSelectedEmployee(emp);
    setEosType('termination');
    setShowEosDialog(true);
    try {
      const { data } = await axios.get(`${API}/employees/${emp.id}/eos?termination_type=termination`, { withCredentials: true });
      setEosData(data);
    } catch (e) { console.error(e); }
  };

  const handleEosTypeChange = async (type) => {
    setEosType(type);
    try {
      const { data } = await axios.get(`${API}/employees/${selectedEmployee.id}/eos?termination_type=${type}`, { withCredentials: true });
      setEosData(data);
    } catch (e) { console.error(e); }
  };

  const calcTotal = () => {
    const b = parseFloat(formData.basic_salary) || 0;
    const h = parseFloat(formData.housing_allowance) || 0;
    const tr = parseFloat(formData.transport_allowance) || 0;
    const o = parseFloat(formData.other_allowances) || 0;
    return b + h + tr + o;
  };

  const handleResetPasswordLetter = async () => {
    setResettingPassword(true);
    try {
      const response = await axios.post(
        `${API}/employees/${selectedEmployee.id}/reset-password-letter`,
        {},
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const fname = selectedEmployee.name_en || selectedEmployee.name || 'employee';
      link.setAttribute('download', `welcome_letter_${fname.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setShowResetPasswordDialog(false);
      toast.success(language === 'ar' ? 'تم إعادة تعيين كلمة المرور وتحميل الخطاب بنجاح' : 'Password reset and letter downloaded successfully');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(t('error'));
    } finally {
      setResettingPassword(false);
    }
  };

  const filteredEmployees = employees.filter(emp => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term ||
      emp.name?.toLowerCase().includes(term) ||
      emp.name_en?.toLowerCase().includes(term) ||
      emp.iqama_number?.toLowerCase().includes(term) ||
      emp.email?.toLowerCase().includes(term) ||
      emp.department?.toLowerCase().includes(term) ||
      emp.employee_number?.toLowerCase().includes(term);
    const matchesNationality = !nationalityFilter || emp.nationality === nationalityFilter;
    const matchesDept = !departmentFilter || emp.department === departmentFilter;
    const empType = emp.employment_type || 'on_site';
    let matchesType = true;
    if (employmentTypeFilter === 'off_site') {
      matchesType = empType !== 'on_site';
    } else if (employmentTypeFilter) {
      matchesType = empType === employmentTypeFilter;
    }
    return matchesSearch && matchesNationality && matchesDept && matchesType;
  });

  const offSiteCount = employees.filter(e => (e.employment_type || 'on_site') !== 'on_site').length;

  const fmtSalary = (v) => v ? <>{Number(v).toLocaleString()} <Sar /></> : '-';

  const selectedDept = departments.find(d => d.value === formData.department);

  const tabOrder = ['basic', 'contact', 'identity', 'contract', 'salary', 'schedule', 'history'];
  const tabOrderForAdd = tabOrder; // same — last tab unlocks after employee is created
  const goNextTab = (isEdit = false) => {
    const order = isEdit ? tabOrder : tabOrderForAdd;
    const idx = order.indexOf(activeTab);
    if (idx < order.length - 1) setActiveTab(order[idx + 1]);
  };

  const formFields = (isEdit) => {
    // In Add flow, the "Salary & Balance" tab needs an employeeId — disable
    // it until the employee has actually been created (selectedEmployee.id
    // is set by handleAdd on success).
    const hasEmployeeId = !!selectedEmployee?.id;
    const historyDisabled = !isEdit && !hasEmployeeId;
    // No-paid-leave employees (contracted workers + percentage doctors)
    // accrue no leave — so the Legacy Balance / Yearly History tab is
    // meaningless for them and is hidden entirely.
    const isContracted = !!formData.is_contracted_worker
      || (!!formData.is_doctor && formData.doctor_type === 'percentage');
    return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" dir={direction}>
      <TabsList className={`grid w-full ${isContracted ? 'grid-cols-6' : 'grid-cols-7'} bg-[#E8E6F5] sticky top-0 z-10`}>
        <TabsTrigger value="basic" className="text-[10px] px-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">{t('employee_name')}</TabsTrigger>
        <TabsTrigger value="contact" className="text-[10px] px-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">{t('contact_preferences')}</TabsTrigger>
        <TabsTrigger value="identity" className="text-[10px] px-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">{t('identity_info')}</TabsTrigger>
        <TabsTrigger value="contract" className="text-[10px] px-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">{t('contract_info')}</TabsTrigger>
        <TabsTrigger value="salary" className="text-[10px] px-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white">{t('salary_details')}</TabsTrigger>
        <TabsTrigger value="schedule" className="text-[10px] px-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white" data-testid="tab-schedule">{language === 'ar' ? 'الجدول' : 'Schedule'}</TabsTrigger>
        {!isContracted && (
          <TabsTrigger
            value="history"
            disabled={historyDisabled}
            className="text-[10px] px-1 data-[state=active]:bg-[#655dc6] data-[state=active]:text-white disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="tab-history"
            title={historyDisabled ? (language === 'ar' ? 'يُفعّل بعد حفظ بيانات الموظف' : 'Unlocks after saving employee') : ''}
          >
            {language === 'ar' ? 'الرصيد التاريخي' : 'Legacy Balance'}
          </TabsTrigger>
        )}
      </TabsList>

      <BasicTab formData={formData} setFormData={setFormData} formErrors={formErrors}
        t={t} language={language} direction={direction} isEdit={isEdit}
        generatePassword={generatePassword} selectedDept={selectedDept} />
      <ContactTab formData={formData} setFormData={setFormData} formErrors={formErrors} t={t} />
      <IdentityTab formData={formData} setFormData={setFormData} formErrors={formErrors} t={t} direction={direction} />
      <ContractTab formData={formData} setFormData={setFormData} formErrors={formErrors} t={t} direction={direction} />
      <SalaryTab formData={formData} setFormData={setFormData} formErrors={formErrors} t={t} calcTotal={calcTotal} language={language} />
      <ScheduleTab formData={formData} setFormData={setFormData} language={language} />
      {hasEmployeeId && !isContracted && <YearlyHistoryTab employeeId={selectedEmployee?.id} language={language} />}
    </Tabs>
  );
  };

  if (loading) {
    return <LucentLoader />;
  }

  return (
    <div className="min-h-screen bg-[#F2F0FA]" data-testid="employees-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-3xl font-semibold text-[#655dc6]">{t('employees')}</h1>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant={viewMode === 'active' ? 'default' : 'outline'}
              onClick={() => setViewMode('active')}
              className={viewMode === 'active' ? 'bg-[#655dc6] hover:bg-[#544cb0] text-white' : 'border-[#655dc6] text-[#655dc6] hover:bg-[#E8E6F5]'}
              data-testid="view-active-employees-btn"
            >
              {language === 'ar' ? 'الموظفون النشطون' : 'Active Employees'}
            </Button>
            <Button
              variant={viewMode === 'terminated' ? 'default' : 'outline'}
              onClick={() => { setViewMode('terminated'); fetchTerminated(); }}
              className={viewMode === 'terminated' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'border-amber-600 text-amber-700 hover:bg-amber-50'}
              data-testid="view-terminated-employees-btn"
            >
              {language === 'ar' ? 'انتهت علاقتهم التعاقدية' : 'Terminated Employees'}
              {terminatedList.length > 0 && (
                <Badge className="ms-2 bg-white/20 text-white">{terminatedList.length}</Badge>
              )}
            </Button>
            {viewMode === 'active' && (
              <>
                <Button variant="outline" onClick={() => setShowImportDialog(true)} className="border-[#655dc6] text-[#655dc6] hover:bg-[#E8E6F5]" data-testid="import-employees-btn">
                  <FileXls size={20} className="me-2" weight="duotone" />
                  {language === 'ar' ? 'استيراد Excel' : 'Import Excel'}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleBulkIssueCertificates}
                  disabled={bulkCertsBusy}
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                  data-testid="bulk-issue-certificates-btn"
                  title={language === 'ar'
                    ? 'تنزيل ملف ZIP بشهادات التدريب لكل موظف مباشر قبل 30/07/2026 — بدون إرسال أي إيميل أو إشعار'
                    : 'Download a ZIP of training certificates for every employee hired before 30/07/2026 — no emails or notifications sent'}
                >
                  {bulkCertsBusy
                    ? (language === 'ar' ? 'جارٍ التجهيز…' : 'Preparing…')
                    : (language === 'ar' ? 'تنزيل شهادات الموظفين' : 'Download Employee Certificates')}
                </Button>
                <Button onClick={() => { setFormData({ ...defaultForm, password: generatePassword() }); setFormErrors({}); setActiveTab('basic'); setShowAddDialog(true); }} className="bg-[#655dc6] hover:bg-[#544cb0] text-white" data-testid="add-employee-btn">
                  <UserPlus size={20} className="me-2" />
                  {t('add_employee')}
                </Button>
              </>
            )}
          </div>
        </div>

        {viewMode === 'active' && (
        <Card className="border border-[#C4BFF0]">
          <CardHeader>
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <MagnifyingGlass size={20} className="absolute start-3 top-1/2 -translate-y-1/2 text-[#655dc6]/70" />
                <Input placeholder={t('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="ps-10 bg-[#E8E6F5] border-[#C4BFF0]" data-testid="search-input" />
              </div>
              <div className="min-w-[160px]">
                <Select value={nationalityFilter} onValueChange={(v) => setNationalityFilter(v === '_all' ? '' : v)} dir={direction}>
                  <SelectTrigger className="bg-[#E8E6F5] border-[#C4BFF0]" data-testid="nationality-filter">
                    <SelectValue placeholder={t('all_nationalities')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{t('all_nationalities')}</SelectItem>
                    {NATIONALITIES.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[180px]">
                <Select value={departmentFilter || '_all'} onValueChange={(v) => setDepartmentFilter(v === '_all' ? '' : v)} dir={direction}>
                  <SelectTrigger className="bg-[#E8E6F5] border-[#C4BFF0]" data-testid="department-filter">
                    <SelectValue placeholder={language === 'ar' ? 'كل الأقسام' : 'All departments'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{language === 'ar' ? 'كل الأقسام' : 'All departments'}</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.value} value={d.value}>
                        {language === 'ar' ? (d.label_ar || d.label) : (d.label_en || d.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[180px]">
                <Select
                  value={employmentTypeFilter || '_all'}
                  onValueChange={(v) => setEmploymentTypeFilter(v === '_all' ? '' : v)}
                  dir={direction}
                >
                  <SelectTrigger className="bg-[#E8E6F5] border-[#C4BFF0]" data-testid="employment-type-filter">
                    <SelectValue placeholder={language === 'ar' ? 'كل الأنواع' : 'All types'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">{language === 'ar' ? 'كل الأنواع' : 'All types'}</SelectItem>
                    <SelectItem value="off_site">
                      {language === 'ar'
                        ? `موظفون خارج الدوام (${offSiteCount})`
                        : `Off-site employees (${offSiteCount})`}
                    </SelectItem>
                    <SelectItem value="on_site">{language === 'ar' ? 'حضور منتظم' : 'On-site (regular)'}</SelectItem>
                    <SelectItem value="remote">{language === 'ar' ? 'عن بُعد' : 'Remote'}</SelectItem>
                    <SelectItem value="flexible">{language === 'ar' ? 'دوام مرن' : 'Flexible'}</SelectItem>
                    <SelectItem value="consultant">{language === 'ar' ? 'مستشار / متعاون' : 'Consultant'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Mobile Card List (<640px) */}
            <div className="sm:hidden" data-testid="employees-mobile-wrapper">
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-xs text-[#655dc6]/70">
                  {filteredEmployees.length} {language === 'ar' ? 'موظف' : 'employees'}
                </span>
                <button
                  type="button"
                  onClick={() => setRevealSalary(v => !v)}
                  className="inline-flex items-center gap-1.5 text-xs text-[#655dc6] hover:text-[#544cb0] bg-[#E8E6F5] border border-[#C4BFF0] rounded-md px-3 py-1 min-h-[44px]"
                  title={revealSalary
                    ? (language === 'ar' ? 'إخفاء الرواتب' : 'Hide salaries')
                    : (language === 'ar' ? 'إظهار الرواتب' : 'Reveal salaries')}
                  data-testid="toggle-salary-visibility-mobile"
                >
                  {revealSalary ? <EyeSlash size={14} weight="duotone" /> : <Eye size={14} weight="duotone" />}
                  <span>{revealSalary ? (language === 'ar' ? 'إخفاء الرواتب' : 'Hide salaries') : (language === 'ar' ? 'إظهار الرواتب' : 'Show salaries')}</span>
                </button>
              </div>
              <div className="space-y-3" data-testid="employees-mobile-list">
              {filteredEmployees.length === 0 ? (
                <p className="text-center py-8 text-[#655dc6]/70 text-sm">{t('no_data')}</p>
              ) : (
                filteredEmployees.map((emp) => (
                  <div
                    key={emp.id}
                    data-testid={`employee-card-${emp.id}`}
                    className="bg-white dark:bg-slate-900 border border-[#C4BFF0] dark:border-slate-700 rounded-xl p-4 shadow-sm"
                  >
                    <div className="flex items-start gap-3 pb-3 mb-3 border-b border-[#C4BFF0]/40 dark:border-slate-800/80">
                      <EmployeeAvatar
                        userId={emp.id}
                        name={emp.name_en || emp.name}
                        photoPath={emp.photo_path}
                        photoUpdatedAt={emp.photo_updated_at}
                        size={40}
                        fontClass="text-sm"
                        className="shrink-0"
                        zoomable={isSysAdmin}
                      />
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-bold text-[#262263] dark:text-slate-100 truncate">{emp.name}</h4>
                        {emp.name_en && (
                          <div className="text-[11px] text-[#655dc6]/60 truncate" dir="ltr">{emp.name_en}</div>
                        )}
                        <div className="text-[11px] text-[#655dc6]/70 font-mono mt-0.5">
                          #{emp.employee_number || '-'}
                        </div>
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="flex items-center gap-1.5 flex-wrap mb-3">
                      {emp.is_department_manager && (
                        <span className="text-[10px] bg-[#655dc6]/10 text-[#655dc6] px-1.5 py-0.5 rounded-full whitespace-nowrap">
                          {t('department_manager')}
                        </span>
                      )}
                      {emp.is_contracted_worker && (
                        <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold" data-testid={`contracted-badge-mobile-${emp.id}`}>
                          🤝 {language === 'ar' ? 'أجير' : 'Hired'}
                        </span>
                      )}
                      {emp.is_doctor && emp.doctor_type === 'salary' && (
                        <span className="text-[10px] bg-indigo-100 text-indigo-800 border border-indigo-300 px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold" data-testid={`doctor-salary-badge-mobile-${emp.id}`}>
                          🩺 {language === 'ar' ? 'طبيب راتب' : 'Doctor · Salary'}
                        </span>
                      )}
                      {emp.is_doctor && emp.doctor_type === 'percentage' && (
                        <span className="text-[10px] bg-pink-100 text-pink-800 border border-pink-300 px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold" data-testid={`doctor-percent-badge-mobile-${emp.id}`}>
                          🩺 {language === 'ar' ? 'طبيب نسبة' : 'Doctor · %'}
                        </span>
                      )}
                      {emp.is_doctor && !emp.doctor_type && (
                        <span className="text-[10px] bg-indigo-100 text-indigo-800 border border-indigo-300 px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold" data-testid={`doctor-badge-mobile-${emp.id}`}>
                          🩺 {language === 'ar' ? 'طبيب' : 'Doctor'}
                        </span>
                      )}
                      {overageByUserId[emp.id] > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] bg-red-100 text-red-700 border border-red-300 px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap" data-testid={`negative-balance-badge-mobile-${emp.id}`}>
                          ⚠ {language === 'ar' ? `−${overageByUserId[emp.id]}` : `−${overageByUserId[emp.id]}d`}
                        </span>
                      )}
                      <EmploymentTypeBadge type={emp.employment_type} language={language} />
                    </div>

                    {/* Metadata grid */}
                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                      <div>
                        <span className="block text-[#655dc6]/60 font-medium mb-0.5">{t('department')}</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{getDeptLabel(emp.department, language) || '—'}</span>
                      </div>
                      <div>
                        <span className="block text-[#655dc6]/60 font-medium mb-0.5">{t('position')}</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200">{language === 'en' ? (emp.position_en || emp.position || '—') : (emp.position || '—')}</span>
                      </div>
                      <div>
                        <span className="block text-[#655dc6]/60 font-medium mb-0.5">{t('iqama_number')}</span>
                        <span className="font-semibold text-slate-800 dark:text-slate-200 font-mono">{emp.iqama_number || '—'}</span>
                      </div>
                      <div>
                        <span className="block text-[#655dc6]/60 font-medium mb-0.5">{t('total_salary')}</span>
                        <span
                          className={`font-semibold text-slate-800 dark:text-slate-200 transition-[filter,opacity] duration-200 ${revealSalary ? '' : 'blur-sm select-none pointer-events-none opacity-80'}`}
                          data-testid={`salary-cell-mobile-${emp.id}`}
                        >
                          {fmtSalary(emp.salary)}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="pt-3 flex items-center gap-1.5 flex-wrap border-t border-[#C4BFF0]/40 dark:border-slate-800/80">
                      <Button variant="outline" size="sm" onClick={() => openDetailDialog(emp)} className="min-h-[44px] min-w-[44px] px-2 border-[#C4BFF0] text-[#655dc6]" data-testid={`view-btn-mobile-${emp.id}`} title={language === 'ar' ? 'عرض' : 'View'}>
                        <Eye size={16} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(emp)} className="min-h-[44px] min-w-[44px] px-2 border-[#C4BFF0] text-[#655dc6]" data-testid={`edit-btn-mobile-${emp.id}`} title={language === 'ar' ? 'تعديل' : 'Edit'}>
                        <PencilSimple size={16} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setSelectedEmployee(emp); setShowDocsDialog(true); }} className="min-h-[44px] min-w-[44px] px-2 border-[#C4BFF0] text-[#655dc6]" data-testid={`docs-btn-mobile-${emp.id}`} title={language === 'ar' ? 'المستندات' : 'Documents'}>
                        <Paperclip size={16} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setSelectedEmployee(emp); setShowResetPasswordDialog(true); }} className="min-h-[44px] min-w-[44px] px-2 border-amber-300 text-amber-700" data-testid={`reset-pwd-btn-mobile-${emp.id}`} title={language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset password'}>
                        <Key size={16} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openEosDialog(emp)} className="min-h-[44px] min-w-[44px] px-2 border-emerald-300 text-emerald-700" data-testid={`eos-btn-mobile-${emp.id}`} title={language === 'ar' ? 'نهاية الخدمة' : 'EOS'}>
                        <Calculator size={16} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openLoginSessions(emp)} className="min-h-[44px] min-w-[44px] px-2 border-sky-300 text-sky-700" data-testid={`login-sessions-btn-mobile-${emp.id}`} title={language === 'ar' ? 'جلسات الدخول' : 'Sessions'}>
                        <ClockClockwise size={16} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setSummonTarget(emp); setSummonAppt(''); setSummonMsg(''); }} className="min-h-[44px] min-w-[44px] px-2 border-purple-300 text-purple-700" data-testid={`summon-btn-mobile-${emp.id}`} title={language === 'ar' ? 'طلب حضور' : 'Summon'}>
                        <Megaphone size={16} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setCertTarget(emp)} className="min-h-[44px] min-w-[44px] px-2 border-indigo-300 text-indigo-700" data-testid={`cert-btn-mobile-${emp.id}`} title={language === 'ar' ? 'شهادة' : 'Certificate'}>
                        <Certificate size={16} />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setSelectedEmployee(emp); setShowDeleteDialog(true); }} className="min-h-[44px] min-w-[44px] px-2 border-red-300 text-red-600 ms-auto" data-testid={`delete-btn-mobile-${emp.id}`} title={language === 'ar' ? 'حذف' : 'Delete'}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </div>
                ))
              )}
              </div>
            </div>

            {/* Desktop Table (>=640px) */}
            <div className="hidden sm:block">
            <ScrollShadow minChildWidth={960}>
              <Table data-testid="employees-table">
                <TableHeader>
                  <TableRow className="border-[#C4BFF0]">
                    <TableHead className="text-start">{t('employee_number')}</TableHead>
                    <TableHead className="text-start">{t('employee_name')}</TableHead>
                    <TableHead className="text-start">{t('department')}</TableHead>
                    <TableHead className="text-start">{t('position')}</TableHead>
                    <TableHead className="text-start">{t('iqama_number')}</TableHead>
                    <TableHead className="text-start">
                      <div className="inline-flex items-center gap-1.5">
                        <span>{t('total_salary')}</span>
                        <button
                          type="button"
                          onClick={() => setRevealSalary(v => !v)}
                          className="text-[#655dc6]/60 hover:text-[#655dc6] transition-colors"
                          title={revealSalary
                            ? (language === 'ar' ? 'إخفاء الرواتب' : 'Hide salaries')
                            : (language === 'ar' ? 'إظهار الرواتب' : 'Reveal salaries')}
                          data-testid="toggle-salary-visibility"
                        >
                          {revealSalary ? <EyeSlash size={16} weight="duotone" /> : <Eye size={16} weight="duotone" />}
                        </button>
                      </div>
                    </TableHead>
                    <TableHead className="text-start">{t('actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-[#655dc6]/70">{t('no_data')}</TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map((emp) => (
                      <TableRow key={emp.id} className="border-[#C4BFF0]" data-testid={`employee-row-${emp.id}`}>
                        <TableCell className="text-[#655dc6]/70">{emp.employee_number || '-'}</TableCell>
                        <TableCell className="font-medium text-[#655dc6]">
                          <div className="flex items-start justify-between gap-3">
                            {/* Right side (RTL start): avatar + names stacked */}
                            <div className="flex items-start gap-2 min-w-0">
                              <EmployeeAvatar
                                userId={emp.id}
                                name={emp.name_en || emp.name}
                                photoPath={emp.photo_path}
                                photoUpdatedAt={emp.photo_updated_at}
                                size={32}
                                fontClass="text-xs"
                                className="shrink-0 mt-0.5"
                                zoomable={isSysAdmin}
                              />
                              <div className="flex flex-col leading-tight min-w-0">
                                <span className="truncate">{emp.name}</span>
                                {emp.name_en && (
                                  <span className="text-[10px] text-[#655dc6]/60 font-normal truncate" dir="ltr">
                                    {emp.name_en}
                                  </span>
                                )}
                                {emp.employment_type && emp.employment_type !== 'on_site' && emp.employment_type_reason && (
                                  <p className="text-[10px] text-[#655dc6]/50 mt-0.5 truncate max-w-[260px]" title={emp.employment_type_reason}>
                                    {emp.employment_type_reason}
                                  </p>
                                )}
                              </div>
                            </div>
                            {/* Left side (RTL end): status badges */}
                            <div className="flex items-center gap-1.5 flex-wrap justify-end shrink-0 pt-0.5">
                              {emp.is_department_manager && (
                                <span className="text-[10px] bg-[#655dc6]/10 text-[#655dc6] px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                  {t('department_manager')}
                                </span>
                              )}
                              {emp.is_contracted_worker && (
                                <span
                                  className="text-[10px] bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold"
                                  title={language === 'ar' ? 'موظف عبر مورد قوى عاملة (أجير)' : 'Agency contracted worker (Hired)'}
                                  data-testid={`contracted-badge-${emp.id}`}
                                >
                                  🤝 {language === 'ar' ? 'أجير' : 'Hired'}
                                </span>
                              )}
                              {emp.is_doctor && emp.doctor_type === 'salary' && (
                                <span
                                  className="text-[10px] bg-indigo-100 text-indigo-800 border border-indigo-300 px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold"
                                  title={language === 'ar' ? 'طبيب على نظام الراتب الثابت' : 'Doctor — fixed salary'}
                                  data-testid={`doctor-salary-badge-${emp.id}`}
                                >
                                  🩺 {language === 'ar' ? 'طبيب راتب' : 'Doctor · Salary'}
                                </span>
                              )}
                              {emp.is_doctor && emp.doctor_type === 'percentage' && (
                                <span
                                  className="text-[10px] bg-pink-100 text-pink-800 border border-pink-300 px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold"
                                  title={language === 'ar' ? 'طبيب على نظام النسبة' : 'Doctor — percentage share'}
                                  data-testid={`doctor-percent-badge-${emp.id}`}
                                >
                                  🩺 {language === 'ar' ? 'طبيب نسبة' : 'Doctor · %'}
                                </span>
                              )}
                              {emp.is_doctor && !emp.doctor_type && (
                                <span
                                  className="text-[10px] bg-indigo-100 text-indigo-800 border border-indigo-300 px-1.5 py-0.5 rounded-full whitespace-nowrap font-semibold"
                                  title={language === 'ar' ? 'طبيب' : 'Doctor'}
                                  data-testid={`doctor-badge-${emp.id}`}
                                >
                                  🩺 {language === 'ar' ? 'طبيب' : 'Doctor'}
                                </span>
                              )}
                              {overageByUserId[emp.id] > 0 && (
                                <span
                                  className="inline-flex items-center gap-1 text-[10px] bg-red-100 text-red-700 border border-red-300 px-1.5 py-0.5 rounded-full font-semibold whitespace-nowrap"
                                  title={language === 'ar'
                                    ? `الموظف تجاوز رصيده السنوي بمقدار ${overageByUserId[emp.id]} يوم. راجع قبل اعتماد إجازات جديدة.`
                                    : `Employee exceeded yearly entitlement by ${overageByUserId[emp.id]} days. Review before approving new leave.`}
                                  data-testid={`negative-balance-badge-${emp.id}`}
                                >
                                  ⚠ {language === 'ar' ? `رصيد سالب −${overageByUserId[emp.id]}` : `Negative −${overageByUserId[emp.id]}d`}
                                </span>
                              )}
                              <EmploymentTypeBadge type={emp.employment_type} language={language} />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-[#655dc6]/70">{getDeptLabel(emp.department, language)}</TableCell>
                        <TableCell className="text-[#655dc6]/70">{language === 'en' ? (emp.position_en || emp.position || '-') : (emp.position || '-')}</TableCell>
                        <TableCell className="text-[#655dc6]/70">{emp.iqama_number || '-'}</TableCell>
                        <TableCell className="text-[#655dc6]/70">
                          <span
                            className={`inline-block transition-[filter,opacity] duration-200 ${revealSalary ? '' : 'blur-sm select-none pointer-events-none opacity-80'}`}
                            data-testid={`salary-cell-${emp.id}`}
                          >
                            {fmtSalary(emp.salary)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" onClick={() => openDetailDialog(emp)} className="h-8 w-8 text-[#655dc6]/70 hover:text-[#655dc6]" data-testid={`view-btn-${emp.id}`}>
                              <Eye size={18} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(emp)} className="h-8 w-8 text-[#655dc6]/70 hover:text-[#655dc6]" data-testid={`edit-btn-${emp.id}`}>
                              <PencilSimple size={18} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedEmployee(emp); setShowDocsDialog(true); }} className="h-8 w-8 text-[#655dc6]/70 hover:text-[#655dc6]" data-testid={`docs-btn-${emp.id}`} title={language === 'ar' ? 'المستندات المرفقة' : 'Attached documents'}>
                              <Paperclip size={18} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedEmployee(emp); setShowResetPasswordDialog(true); }} className="h-8 w-8 text-[#655dc6]/70 hover:text-amber-600" data-testid={`reset-pwd-btn-${emp.id}`} title={language === 'ar' ? 'إعادة تعيين كلمة المرور وطباعة خطاب' : 'Reset password & print letter'}>
                              <Key size={18} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openEosDialog(emp)} className="h-8 w-8 text-[#655dc6]/70 hover:text-green-600" data-testid={`eos-btn-${emp.id}`}>
                              <Calculator size={18} />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => openLoginSessions(emp)}
                              className="h-8 w-8 text-[#655dc6]/70 hover:text-sky-600"
                              data-testid={`login-sessions-btn-${emp.id}`}
                              title={language === 'ar' ? 'جلسات تسجيل الدخول' : 'Login sessions'}
                            >
                              <ClockClockwise size={18} />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => { setSummonTarget(emp); setSummonAppt(''); setSummonMsg(''); }}
                              className="h-8 w-8 text-[#655dc6]/70 hover:text-purple-700"
                              data-testid={`summon-btn-${emp.id}`}
                              title={language === 'ar' ? 'طلب حضور لمكتب شؤون الموظفين' : 'Request HR Office Visit'}
                            >
                              <Megaphone size={18} />
                            </Button>
                            <Button
                              variant="ghost" size="icon"
                              onClick={() => setCertTarget(emp)}
                              className="h-8 w-8 text-[#655dc6]/70 hover:text-indigo-700"
                              data-testid={`cert-btn-${emp.id}`}
                              title={language === 'ar' ? 'إصدار شهادة تدريبية' : 'Issue training certificate'}
                            >
                              <Certificate size={18} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedEmployee(emp); setShowDeleteDialog(true); }} className="h-8 w-8 text-[#655dc6]/70 hover:text-red-600" data-testid={`delete-btn-${emp.id}`}>
                              <Trash size={18} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </ScrollShadow>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Terminated Employees — read-only roster */}
        {viewMode === 'terminated' && (
          <Card className="border border-amber-200">
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-amber-700">
                    {language === 'ar' ? 'موظفون انتهت علاقتهم التعاقدية' : 'Terminated Employees'}
                  </h2>
                  <p className="text-xs text-amber-700/70 mt-1">
                    {language === 'ar'
                      ? 'سجل أرشيفي للموظفين السابقين. لا يمكنهم تسجيل الدخول. يمكن إعادة التعيين عند العودة.'
                      : 'Archive of former employees. They cannot log in. They can be reinstated upon rehire.'}
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Mobile Card List (<640px) */}
              <div className="sm:hidden space-y-3" data-testid="terminated-mobile-list">
                {terminatedList.length === 0 ? (
                  <p className="text-center py-8 text-[#655dc6]/40 text-sm">
                    {language === 'ar' ? 'لا يوجد موظفون منتهون حالياً' : 'No terminated employees yet'}
                  </p>
                ) : (
                  terminatedList.map((emp) => (
                    <div
                      key={emp.id}
                      data-testid={`terminated-card-${emp.id}`}
                      className="bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-900/40 rounded-xl p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-2 pb-3 mb-3 border-b border-amber-100 dark:border-amber-900/30">
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-bold text-[#262263] dark:text-slate-100">{emp.name}</h4>
                          {emp.name_en && <div className="text-[11px] text-[#655dc6]/60" dir="ltr">{emp.name_en}</div>}
                          <div className="text-[11px] text-[#655dc6]/70 font-mono mt-0.5">#{emp.employee_number || '-'}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                        <div>
                          <span className="block text-[#655dc6]/60 font-medium mb-0.5">{t('department')}</span>
                          <span className="font-semibold text-slate-800 dark:text-slate-200">{emp.department || '—'}</span>
                        </div>
                        <div>
                          <span className="block text-[#655dc6]/60 font-medium mb-0.5">{language === 'ar' ? 'تاريخ الإنهاء' : 'Termination date'}</span>
                          <span className="font-semibold text-slate-800 dark:text-slate-200 tabular-nums">{emp.terminated_at || '—'}</span>
                        </div>
                        {emp.termination_reason_text && (
                          <div className="col-span-2">
                            <span className="block text-[#655dc6]/60 font-medium mb-0.5">{language === 'ar' ? 'السبب' : 'Reason'}</span>
                            <span className="text-slate-700 dark:text-slate-300 line-clamp-2">{emp.termination_reason_text}</span>
                          </div>
                        )}
                      </div>
                      <div className="pt-3 flex items-center gap-2 border-t border-amber-100 dark:border-amber-900/30">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetailDialog(emp)}
                          className="flex-1 min-h-[44px] border-[#C4BFF0] text-[#655dc6] gap-1.5"
                          data-testid={`view-terminated-btn-mobile-${emp.id}`}
                        >
                          <User size={16} />
                          {language === 'ar' ? 'عرض' : 'View'}
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleReinstate(emp)}
                          className="flex-1 min-h-[44px] bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                          data-testid={`reinstate-btn-mobile-${emp.id}`}
                        >
                          <ArrowsClockwise size={16} />
                          {language === 'ar' ? 'إعادة تفعيل' : 'Reinstate'}
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop Table (>=640px) */}
              <div className="hidden sm:block">
              <ScrollShadow minChildWidth={800}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-start text-[#655dc6]">{language === 'ar' ? 'الرقم' : '#'}</TableHead>
                      <TableHead className="text-start text-[#655dc6]">{t('employee_name')}</TableHead>
                      <TableHead className="text-start text-[#655dc6]">{t('department')}</TableHead>
                      <TableHead className="text-start text-[#655dc6]">{language === 'ar' ? 'تاريخ الإنهاء' : 'Termination date'}</TableHead>
                      <TableHead className="text-start text-[#655dc6]">{language === 'ar' ? 'السبب' : 'Reason'}</TableHead>
                      <TableHead className="text-start text-[#655dc6]">{t('actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {terminatedList.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-[#655dc6]/40 py-10">
                          {language === 'ar' ? 'لا يوجد موظفون منتهون حالياً' : 'No terminated employees yet'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      terminatedList.map((emp) => (
                        <TableRow key={emp.id} data-testid={`terminated-row-${emp.id}`}>
                          <TableCell className="text-[#655dc6]/70 tabular-nums">{emp.employee_number || '-'}</TableCell>
                          <TableCell>
                            <div className="font-medium text-[#655dc6]">{emp.name}</div>
                            {emp.name_en && <div className="text-[10px] text-[#655dc6]/60" dir="ltr">{emp.name_en}</div>}
                          </TableCell>
                          <TableCell className="text-[#655dc6]/80">{emp.department || '-'}</TableCell>
                          <TableCell className="text-[#655dc6]/80 tabular-nums">{emp.terminated_at || '-'}</TableCell>
                          <TableCell className="text-[#655dc6]/80 max-w-[260px] truncate" title={emp.termination_reason_text}>
                            {emp.termination_reason_text || '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost" size="icon"
                                onClick={() => openDetailDialog(emp)}
                                className="h-8 w-8 text-[#655dc6]/70 hover:text-[#655dc6]"
                                data-testid={`view-terminated-btn-${emp.id}`}
                                title={language === 'ar' ? 'عرض البيانات' : 'View details'}
                              >
                                <User size={16} />
                              </Button>
                              <Button
                                variant="ghost" size="icon"
                                onClick={() => handleReinstate(emp)}
                                className="h-8 w-8 text-[#655dc6]/70 hover:text-green-600"
                                data-testid={`reinstate-btn-${emp.id}`}
                                title={language === 'ar' ? 'إعادة تفعيل (عودة للعمل)' : 'Reinstate (rehire)'}
                              >
                                <ArrowsClockwise size={16} />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </ScrollShadow>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add Employee Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="sm:max-w-[850px] max-h-[90vh] flex flex-col" dir={direction} data-testid="add-employee-dialog">
            <div dir={direction} style={{ direction }} className="flex flex-col flex-1 min-h-0">
              <DialogHeader>
                <DialogTitle>{t('add_employee')}</DialogTitle>
                <DialogDescription>{direction === 'rtl' ? 'أدخل بيانات الموظف الجديد عبر التبويبات' : 'Enter the new employee details across the tabs'}</DialogDescription>
              </DialogHeader>
              <div className="mt-3 flex-1 min-h-0 overflow-y-auto pe-1">
                {formFields(false)}
              </div>
              <DialogFooter className="mt-4 pt-3 border-t border-[#C4BFF0]/30 shrink-0">
                {activeTab === 'history' ? (
                  <Button
                    onClick={() => {
                      setShowAddDialog(false);
                      setFormData({ ...defaultForm });
                      setSelectedEmployee(null);
                      setActiveTab('basic');
                    }}
                    className="bg-[#655dc6] hover:bg-[#544cb0]"
                    data-testid="finish-add-btn"
                  >
                    {language === 'ar' ? 'إنهاء' : 'Finish'}
                  </Button>
                ) : activeTab === 'schedule' ? (
                  selectedEmployee?.id ? (
                    <Button onClick={() => goNextTab(false)} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="next-tab-btn">
                      {t('next')}
                    </Button>
                  ) : (
                    <Button onClick={handleAdd} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="save-employee-btn">
                      {saving ? <SpinnerGap size={20} className="animate-spin" /> : (language === 'ar' ? 'حفظ ومتابعة' : 'Save & Continue')}
                    </Button>
                  )
                ) : (
                  <Button onClick={() => goNextTab(false)} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="next-tab-btn">
                    {t('next')}
                  </Button>
                )}
                <Button variant="outline" onClick={() => {
                  setShowAddDialog(false);
                  setFormData({ ...defaultForm });
                  setSelectedEmployee(null);
                  setActiveTab('basic');
                }}>{t('cancel')}</Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Employee Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[850px] max-h-[90vh] flex flex-col" dir={direction} data-testid="edit-employee-dialog">
            <div dir={direction} style={{ direction }} className="flex flex-col flex-1 min-h-0">
              <DialogHeader>
                <DialogTitle>{t('edit_employee')}</DialogTitle>
                <DialogDescription>{direction === 'rtl' ? 'عدّل بيانات الموظف عبر التبويبات' : 'Update employee details across the tabs'}</DialogDescription>
              </DialogHeader>
              <div className="mt-3 flex-1 min-h-0 overflow-y-auto pe-1">
                {formFields(true)}
              </div>
              <DialogFooter className="mt-4 pt-3 border-t border-[#C4BFF0]/30 shrink-0">
                {activeTab === 'history' ? (
                  <Button onClick={handleEdit} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0]" data-testid="save-edit-btn">
                    {saving ? <SpinnerGap size={20} className="animate-spin" /> : t('save_changes')}
                  </Button>
                ) : (
                  <Button onClick={() => goNextTab(true)} className="bg-[#655dc6] hover:bg-[#544cb0]">
                    {t('next')}
                  </Button>
                )}
                <Button variant="outline" onClick={() => { setShowEditDialog(false); setActiveTab('basic'); }}>{t('cancel')}</Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Employee Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="sm:max-w-[650px] max-h-[85vh]" dir={direction} data-testid="detail-employee-dialog">
            <div dir={direction} style={{ direction }}>
              <DialogHeader>
                <DialogTitle className="text-[#655dc6]">{selectedEmployee?.name}</DialogTitle>
                <DialogDescription>{direction === 'rtl' ? 'تفاصيل الموظف ورصيد الإجازات' : 'Employee details and leave balance'}</DialogDescription>
              </DialogHeader>
            </div>
            {selectedEmployee && (
              <ScrollArea className="max-h-[65vh] pe-4">
                <div className="space-y-5" dir={direction} style={{ direction }}>
                  {/* Basic Info */}
                  <div>
                    <h3 className="text-sm font-semibold text-[#655dc6] mb-2 text-start">{t('employee_name')}</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <InfoRow label={t('employee_name')} value={selectedEmployee.name} />
                      <InfoRow label={t('employee_name_en')} value={selectedEmployee.name_en} />
                      <InfoRow label={t('email')} value={selectedEmployee.email} />
                      <InfoRow label={t('phone')} value={selectedEmployee.phone} />
                      <InfoRow label={t('birth_date')} value={selectedEmployee.birth_date} />
                      <InfoRow label={t('department')} value={getDeptLabel(selectedEmployee.department, language)} />
                      <InfoRow label={t('position')} value={language === 'en' ? (selectedEmployee.position_en || selectedEmployee.position) : selectedEmployee.position} />
                    </div>
                  </div>
                  <Separator />
                  {/* Identity */}
                  <div>
                    <h3 className="text-sm font-semibold text-[#655dc6] mb-2 text-start">{t('identity_info')}</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <InfoRow label={t('iqama_number')} value={selectedEmployee.iqama_number} />
                      <InfoRow label={t('employee_number')} value={selectedEmployee.employee_number} />
                      <InfoRow label={t('nationality')} value={selectedEmployee.nationality} />
                    </div>
                  </div>
                  <Separator />
                  {/* Contract */}
                  <div>
                    <h3 className="text-sm font-semibold text-[#655dc6] mb-2 text-start">{t('contract_info')}</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <InfoRow label={t('hire_date')} value={selectedEmployee.hire_date} />
                      <InfoRow label={t('contract_start_date')} value={selectedEmployee.contract_start_date} />
                      <InfoRow label={t('contract_end_date')} value={selectedEmployee.contract_end_date} />
                      <InfoRow label={t('qiwa_status')} value={selectedEmployee.qiwa_status === 'active' ? t('active_on_qiwa') : t('not_on_qiwa')} />
                      <InfoRow
                        label={language === 'ar' ? 'طبيعة التعاقد' : 'Employment type'}
                        value={(() => {
                          const v = selectedEmployee.employment_type || 'on_site';
                          if (language === 'ar') {
                            return ({ on_site: 'حضور منتظم', remote: 'عن بُعد', flexible: 'دوام مرن', consultant: 'مستشار / متعاون', attendance_based: 'على الحضور فقط (يومي)', part_time: 'دوام جزئي' })[v] || v;
                          }
                          return ({ on_site: 'On-site', remote: 'Remote', flexible: 'Flexible', consultant: 'Consultant', attendance_based: 'Attendance-only (per-day)', part_time: 'Part-time' })[v] || v;
                        })()}
                      />
                      {selectedEmployee.employment_type && selectedEmployee.employment_type !== 'on_site' && (
                        <InfoRow
                          label={language === 'ar' ? 'سبب التصنيف' : 'Reason'}
                          value={selectedEmployee.employment_type_reason || '-'}
                        />
                      )}
                    </div>
                  </div>
                  <Separator />
                  {/* Salary */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-[#655dc6]">{t('salary_details')}</h3>
                      <button
                        type="button"
                        onClick={() => setRevealSalary(v => !v)}
                        className="inline-flex items-center gap-1 text-xs text-[#655dc6]/70 hover:text-[#655dc6] transition-colors"
                        data-testid="detail-toggle-salary-visibility"
                      >
                        {revealSalary ? <EyeSlash size={14} weight="duotone" /> : <Eye size={14} weight="duotone" />}
                        {revealSalary
                          ? (language === 'ar' ? 'إخفاء' : 'Hide')
                          : (language === 'ar' ? 'إظهار' : 'Reveal')}
                      </button>
                    </div>
                    <div className={`transition-[filter,opacity] duration-200 ${revealSalary ? '' : 'blur-sm select-none pointer-events-none opacity-80'}`}>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <InfoRow label={t('basic_salary')} value={fmtSalary(selectedEmployee.basic_salary)} />
                        <InfoRow label={t('housing_allowance')} value={fmtSalary(selectedEmployee.housing_allowance)} />
                        <InfoRow label={t('transport_allowance')} value={fmtSalary(selectedEmployee.transport_allowance)} />
                        <InfoRow label={t('other_allowances')} value={fmtSalary(selectedEmployee.other_allowances)} />
                      </div>
                      {selectedEmployee.nationality === 'سعودي' && (selectedEmployee.gosi_subscription || 0) > 0 && (
                        <div className="mt-2 p-2 bg-rose-50 border border-rose-200 rounded-lg flex justify-between text-sm" data-testid="gosi-row">
                          <span className="font-medium text-rose-800">
                            {language === 'ar' ? 'تأمينات اجتماعية (GOSI)' : 'Social Insurance (GOSI)'}
                          </span>
                          <span className="font-bold text-rose-800">− {fmtSalary(selectedEmployee.gosi_subscription)}</span>
                        </div>
                      )}
                      <div className="mt-3 p-3 bg-[#E8E6F5] rounded-lg flex justify-between">
                        <span className="font-medium text-[#655dc6]">{t('total_salary')}</span>
                        <span className="font-bold text-[#655dc6]">{fmtSalary(selectedEmployee.salary)}</span>
                      </div>
                    </div>
                  </div>
                  <Separator />
                  {/* Weekly Schedule (Sat → Thu) */}
                  <div>
                    <h3 className="text-sm font-semibold text-[#655dc6] mb-2 text-start">
                      {language === 'ar' ? 'أوقات العمل اليومية' : 'Weekly Working Hours'}
                    </h3>
                    <EmployeeWeeklyScheduleView employee={selectedEmployee} language={language} />
                  </div>
                  <Separator />
                  {/* Leave Balance */}
                  {leaveBalance && (
                    <div>
                      <h3 className="text-sm font-semibold text-[#655dc6] mb-2 text-start">{t('leave_balance')}</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 bg-[#E8E6F5] rounded-lg text-center">
                          <p className="text-xs text-[#655dc6]/70">{t('annual_entitlement')}</p>
                          <p className="text-xl font-bold text-[#655dc6]">{leaveBalance.annual_entitlement} {t('days')}</p>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg text-center">
                          <p className="text-xs text-green-600">{t('remaining_days')}</p>
                          <p className="text-xl font-bold text-green-600">{leaveBalance.remaining_days} {t('days')}</p>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg text-center">
                          <p className="text-xs text-red-600">{t('used_days')}</p>
                          <p className="text-xl font-bold text-red-600">{leaveBalance.used_days} {t('days')}</p>
                        </div>
                        <div className="p-3 bg-yellow-50 rounded-lg text-center">
                          <p className="text-xs text-yellow-600">{t('pending_days')}</p>
                          <p className="text-xl font-bold text-yellow-600">{leaveBalance.pending_days} {t('days')}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  <Separator />
                  {/* EOS */}
                  {eosData && (
                    <div>
                      <h3 className="text-sm font-semibold text-[#655dc6] mb-2 text-start">{t('eos_calculation')}</h3>
                      <div className="p-4 bg-gradient-to-br from-[#655dc6] to-[#544cb0] rounded-lg text-white">
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-white/80 text-sm">{t('eos_amount')}</span>
                          <span className="text-2xl font-bold">{eosData.eos_amount?.toLocaleString()} <Sar /></span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm text-white/80">
                          <span>{t('years_of_service')}: {eosData.years_of_service}</span>
                          <span>{t('daily_wage')}: {eosData.daily_wage} <Sar /></span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </DialogContent>
        </Dialog>

        {/* EOS Calculator Dialog */}
        <Dialog open={showEosDialog} onOpenChange={setShowEosDialog}>
          <DialogContent className="sm:max-w-[500px]" dir={direction} data-testid="eos-dialog">
            <div dir={direction} style={{ direction }}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
                  <Calculator size={22} weight="duotone" />
                  {t('eos_calculation')} - {selectedEmployee?.name}
                </DialogTitle>
                <DialogDescription>{direction === 'rtl' ? 'احتساب نهاية الخدمة بناءً على نوع الإنهاء' : 'End of service calculation based on termination type'}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
              <div className="space-y-1">
                <Label className="text-xs text-start block">{t('termination')}/{t('resignation')}</Label>
                <Select value={eosType} onValueChange={handleEosTypeChange} dir={direction}>
                  <SelectTrigger data-testid="eos-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="termination">{t('termination')}</SelectItem>
                    <SelectItem value="resignation">{t('resignation')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {eosData && (
                <div className="space-y-3">
                  <div className="p-4 bg-gradient-to-br from-[#655dc6] to-[#544cb0] rounded-xl text-white text-center">
                    <p className="text-white/80 text-sm mb-1">{t('eos_amount')}</p>
                    <p className="text-3xl font-bold" data-testid="eos-amount">{eosData.eos_amount?.toLocaleString()} <Sar /></p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-[#E8E6F5] rounded-lg text-center">
                      <p className="text-xs text-[#655dc6]/70">{t('years_of_service')}</p>
                      <p className="text-lg font-bold text-[#655dc6]">{eosData.years_of_service}</p>
                    </div>
                    <div className="p-3 bg-[#E8E6F5] rounded-lg text-center">
                      <p className="text-xs text-[#655dc6]/70">{t('daily_wage')}</p>
                      <p className="text-lg font-bold text-[#655dc6]">{eosData.daily_wage} <Sar /></p>
                    </div>
                    <div className="p-3 bg-[#E8E6F5] rounded-lg text-center">
                      <p className="text-xs text-[#655dc6]/70">{t('first_5_years')}</p>
                      <p className="text-lg font-bold text-[#655dc6]">{eosData.first_5_years_amount?.toLocaleString()} <Sar /></p>
                    </div>
                    <div className="p-3 bg-[#E8E6F5] rounded-lg text-center">
                      <p className="text-xs text-[#655dc6]/70">{t('after_5_years')}</p>
                      <p className="text-lg font-bold text-[#655dc6]">{eosData.after_5_years_amount?.toLocaleString()} <Sar /></p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* v1.5.45 — Issue training certificate dialog. */}
        <Dialog open={!!certTarget} onOpenChange={(o) => { if (!o) setCertTarget(null); }}>
          <DialogContent className="sm:max-w-[500px]" dir={direction} data-testid="cert-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#655dc6] flex items-center gap-2">
                <Certificate size={20} weight="duotone" />
                {language === 'ar' ? 'إصدار شهادة تدريبية' : 'Issue Training Certificate'}
              </DialogTitle>
            </DialogHeader>
            {certTarget && (
              <div className="rounded-md bg-[#F2F0FA] border border-[#C4BFF0] px-3 py-2 text-sm text-[#655dc6] mb-2">
                <p className="font-semibold">{certTarget.name_en || certTarget.name}</p>
                {!certTarget.name_en && (
                  <p className="text-[10px] text-red-600 mt-1">
                    {language === 'ar' ? '⚠ لا يوجد اسم إنجليزي مسجّل — سيُطبع الاسم العربي' : '⚠ No English name on file — Arabic will print'}
                  </p>
                )}
              </div>
            )}
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{language === 'ar' ? 'نوع الشهادة' : 'Certificate Type'}</Label>
                <Select value={certType} onValueChange={setCertType}>
                  <SelectTrigger data-testid="cert-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quality_patient_safety">Quality & Patient Safety</SelectItem>
                    <SelectItem value="safety_fire_prevention">Safety Orientation & Fire Prevention</SelectItem>
                    <SelectItem value="infection_control">Infection Control Orientation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{language === 'ar' ? 'تاريخ المنح (Grant Date)' : 'Grant Date'}</Label>
                <Input
                  type="date"
                  value={certGrantDate}
                  onChange={(e) => setCertGrantDate(e.target.value)}
                  data-testid="cert-date-input"
                />
                <p className="text-[10px] text-[#655dc6]/60 mt-1">
                  {language === 'ar'
                    ? 'سيُطبع بالصيغة "August 20, 2026" على الشهادة.'
                    : 'Will be printed as "August 20, 2026" on the certificate.'}
                </p>
              </div>
            </div>
            <DialogFooter className="pt-3">
              <Button variant="outline" onClick={() => setCertTarget(null)}>
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                disabled={certIssuing || !certGrantDate}
                onClick={async () => {
                  setCertIssuing(true);
                  try {
                    const url = `${API}/employees/${certTarget.id}/certificate/${certType}?grant_date=${encodeURIComponent(certGrantDate)}`;
                    // Open in a new tab (also triggers the save to employee documents).
                    window.open(url, '_blank');
                    toast.success(language === 'ar'
                      ? 'تم إصدار الشهادة وحفظ نسخة في مستنداته'
                      : 'Certificate issued and saved to their documents');
                    setCertTarget(null);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed');
                  } finally {
                    setCertIssuing(false);
                  }
                }}
                className="bg-[#655dc6] hover:bg-[#544cb0] text-white"
                data-testid="cert-issue-btn"
              >
                {certIssuing
                  ? (language === 'ar' ? 'جاري الإصدار…' : 'Issuing…')
                  : (language === 'ar' ? 'إصدار وتحميل' : 'Issue & Download')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* HR Summons dialog — one-click call-in for the employee. */}
        <Dialog open={!!summonTarget} onOpenChange={(o) => { if (!o) { setSummonTarget(null); setSummonAppt(''); setSummonMsg(''); } }}>
          <DialogContent className="sm:max-w-[480px]" dir={direction} data-testid="summon-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#655dc6] flex items-center gap-2">
                <Megaphone size={20} weight="duotone" />
                {language === 'ar' ? 'طلب حضور موظف لمكتب شؤون الموظفين' : 'Request Employee HR Office Visit'}
              </DialogTitle>
            </DialogHeader>
            {summonTarget && (
              <div className="rounded-md bg-[#F2F0FA] border border-[#C4BFF0] px-3 py-2 text-sm text-[#655dc6]">
                <p className="font-semibold">{summonTarget.name}</p>
                <p className="text-xs opacity-70">{summonTarget.email}{summonTarget.department ? ` · ${getDeptLabel(summonTarget.department, language)}` : ''}</p>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">
                {language === 'ar' ? 'موعد الحضور (اختياري)' : 'Appointment (optional)'}
              </Label>
              <Input
                type="datetime-local"
                value={summonAppt}
                onChange={(e) => setSummonAppt(e.target.value)}
                className="w-full"
                data-testid="summon-appt-input"
              />
              <p className="text-[10px] text-[#655dc6]/60">
                {language === 'ar'
                  ? 'اتركه فارغاً ليعني "الحضور في أقرب وقت ممكن".'
                  : 'Leave empty to mean "come as soon as possible".'}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {language === 'ar' ? 'ملاحظة للموظف (اختياري)' : 'Note to the employee (optional)'}
              </Label>
              <Textarea
                value={summonMsg}
                onChange={(e) => setSummonMsg(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder={language === 'ar' ? 'مثلاً: يُرجى الحضور لمراجعة العقد' : 'e.g. Please come to review your contract'}
                className="text-sm resize-none"
                data-testid="summon-msg-input"
              />
              <p className="text-[10px] text-[#655dc6]/60 text-end">{summonMsg.length}/500</p>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setSummonTarget(null); setSummonAppt(''); setSummonMsg(''); }}
                disabled={summonSending}
                data-testid="summon-cancel-btn"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={async () => {
                  setSummonSending(true);
                  try {
                    await axios.post(`${API}/hr/summons`, {
                      employee_id: summonTarget.id,
                      appointment_at: summonAppt || null,
                      message: summonMsg.trim() || null,
                    }, { withCredentials: true });
                    toast.success(language === 'ar' ? 'تم إرسال طلب الحضور' : 'Request sent');
                    setSummonTarget(null); setSummonAppt(''); setSummonMsg('');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || (language === 'ar' ? 'تعذّر الإرسال' : 'Send failed'));
                  } finally {
                    setSummonSending(false);
                  }
                }}
                disabled={summonSending}
                className="bg-[#655dc6] hover:bg-[#544cb0] gap-1"
                data-testid="summon-send-btn"
              >
                {summonSending
                  ? <SpinnerGap size={14} className="animate-spin" />
                  : <Megaphone size={14} weight="bold" />}
                {language === 'ar' ? 'إرسال طلب الحضور' : 'Send Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation — two modes:
             • data_entry_error → hard delete (permanent)
             • contract_termination → soft delete (move to Terminated tab) */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent dir={direction} data-testid="delete-confirm-dialog">
            <div dir={direction} style={{ direction }}>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {language === 'ar' ? 'سبب إزالة الموظف' : 'Why are you removing this employee?'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#655dc6]">
                <strong>{selectedEmployee?.name}</strong>
                {selectedEmployee?.name_en && <span className="text-[#655dc6]/60 ms-2" dir="ltr">{selectedEmployee.name_en}</span>}
              </AlertDialogDescription>
            </AlertDialogHeader>

            <div className="space-y-3 my-3">
              <label className="flex items-start gap-2 p-3 rounded-md border border-[#C4BFF0] cursor-pointer hover:bg-[#F4F2FB]"
                     onClick={() => setDeleteReason('data_entry_error')}>
                <input
                  type="radio" name="del-reason" value="data_entry_error"
                  checked={deleteReason === 'data_entry_error'}
                  onChange={() => setDeleteReason('data_entry_error')}
                  className="mt-0.5"
                  data-testid="reason-data-entry-error"
                />
                <div>
                  <div className="font-semibold text-sm text-[#655dc6]">
                    {language === 'ar' ? 'إدخال خاطئ' : 'Data entry error'}
                  </div>
                  <div className="text-[11px] text-[#655dc6]/60 mt-0.5">
                    {language === 'ar'
                      ? 'حذف نهائي للسجل من قاعدة البيانات — لا يمكن التراجع'
                      : 'Permanently removes the record — cannot be undone'}
                  </div>
                </div>
              </label>
              <label className="flex items-start gap-2 p-3 rounded-md border border-[#C4BFF0] cursor-pointer hover:bg-[#F4F2FB]"
                     onClick={() => setDeleteReason('contract_termination')}>
                <input
                  type="radio" name="del-reason" value="contract_termination"
                  checked={deleteReason === 'contract_termination'}
                  onChange={() => setDeleteReason('contract_termination')}
                  className="mt-0.5"
                  data-testid="reason-contract-termination"
                />
                <div>
                  <div className="font-semibold text-sm text-[#655dc6]">
                    {language === 'ar' ? 'إنهاء العلاقة التعاقدية' : 'Contract termination'}
                  </div>
                  <div className="text-[11px] text-[#655dc6]/60 mt-0.5">
                    {language === 'ar'
                      ? 'يُحتفظ بالسجل ضمن قائمة "موظفون انتهت علاقتهم التعاقدية" للمعرفة فقط'
                      : 'Record kept in the Terminated Employees list for reference'}
                  </div>
                </div>
              </label>

              {deleteReason === 'contract_termination' && (
                <div className="space-y-2 p-3 rounded-md bg-[#F4F2FB] border border-[#C4BFF0]">
                  <div className="space-y-1">
                    <label className="text-xs text-[#655dc6] block">
                      {language === 'ar' ? 'تاريخ الإنهاء' : 'Termination date'}
                    </label>
                    <Input
                      type="date" value={terminationDate}
                      onChange={(e) => setTerminationDate(e.target.value)}
                      className="h-9 text-sm"
                      data-testid="termination-date-input"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-[#655dc6] block">
                      {language === 'ar' ? 'ملاحظة (اختياري)' : 'Note (optional)'}
                    </label>
                    <Input
                      value={terminationNote}
                      onChange={(e) => setTerminationNote(e.target.value)}
                      placeholder={language === 'ar' ? 'مثال: استقالة، إنهاء عقد، انتهاء فترة تجريبية' : 'e.g. resignation, contract end, probation ended'}
                      className="h-9 text-sm"
                      data-testid="termination-note-input"
                    />
                  </div>
                </div>
              )}
            </div>

            <AlertDialogFooter>
              <AlertDialogAction
                onClick={handleDelete}
                className={deleteReason === 'contract_termination' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="confirm-delete-btn"
              >
                {deleteReason === 'contract_termination'
                  ? (language === 'ar' ? 'نقل إلى المُنهَى تعاقدهم' : 'Move to Terminated')
                  : (language === 'ar' ? 'حذف نهائي' : 'Delete permanently')}
              </AlertDialogAction>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            </AlertDialogFooter>
            </div>
          </AlertDialogContent>
        </AlertDialog>

        {/* Reset Password & Print Letter Dialog */}
        <AlertDialog open={showResetPasswordDialog} onOpenChange={setShowResetPasswordDialog}>
          <AlertDialogContent dir={direction} data-testid="reset-password-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {language === 'ar' ? 'إعادة تعيين كلمة المرور' : 'Reset Password'}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-start">
                {language === 'ar'
                  ? `سيتم إنشاء كلمة مرور جديدة للموظف "${selectedEmployee?.name}" وتحميل خطاب ترحيب يحتوي على بيانات الدخول الجديدة. سيُطلب من الموظف تغيير كلمة المرور عند أول تسجيل دخول.`
                  : `A new password will be generated for "${selectedEmployee?.name}" and a welcome letter with the new credentials will be downloaded. The employee will be required to change the password on first login.`}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction
                onClick={handleResetPasswordLetter}
                disabled={resettingPassword}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-reset-pwd-btn"
              >
                {resettingPassword
                  ? <SpinnerGap size={18} className="animate-spin" />
                  : (language === 'ar' ? 'إعادة التعيين وطباعة الخطاب' : 'Reset & Print Letter')}
              </AlertDialogAction>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <ImportEmployeesDialog
          open={showImportDialog}
          onOpenChange={setShowImportDialog}
          onImported={fetchEmployees}
          language={language}
        />

        <EmployeeDocumentsDialog
          open={showDocsDialog}
          onOpenChange={setShowDocsDialog}
          employee={selectedEmployee}
          language={language}
          direction={direction}
        />

        {/* Login Sessions dialog — admin-only audit view of who logged in,
            when, from where (IP) and on what device (User-Agent), with
            both successful and failed attempts surfaced. */}
        <Dialog open={showLoginSessionsDialog} onOpenChange={setShowLoginSessionsDialog}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" dir={direction} data-testid="login-sessions-dialog">
            <DialogHeader>
              <DialogTitle className="text-[#655dc6]">
                {language === 'ar' ? 'جلسات تسجيل الدخول' : 'Login Sessions'}
              </DialogTitle>
              {loginSessionsEmp && (
                <p className="text-sm text-[#655dc6]/70">
                  {loginSessionsEmp.name}
                  {loginSessionsEmp.name_en && <span className="ms-2 text-[#655dc6]/50" dir="ltr">{loginSessionsEmp.name_en}</span>}
                </p>
              )}
            </DialogHeader>
            <div className="overflow-auto flex-1 -mx-6 px-6">
              {loadingSessions ? (
                <div className="text-center py-10 text-[#655dc6]/60">
                  <SpinnerGap size={28} className="animate-spin mx-auto" />
                </div>
              ) : loginSessions.length === 0 ? (
                <div className="text-center py-10 text-[#655dc6]/40">
                  {language === 'ar' ? 'لا توجد جلسات لهذا الموظف' : 'No login sessions yet'}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-center text-[#655dc6]">{language === 'ar' ? 'الوقت' : 'When'}</TableHead>
                      <TableHead className="text-center text-[#655dc6]">{language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
                      <TableHead className="text-center text-[#655dc6]">{language === 'ar' ? 'المدينة' : 'City'}</TableHead>
                      <TableHead className="text-center text-[#655dc6]">IP</TableHead>
                      <TableHead className="text-center text-[#655dc6]">{language === 'ar' ? 'الجهاز / المتصفح' : 'Device / Browser'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loginSessions.map((s) => (
                      <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                        <TableCell className="text-center text-[11px] tabular-nums text-[#655dc6]/80" dir="ltr">
                          {formatRiyadh(s.at, language)}
                        </TableCell>
                        <TableCell className="text-center">
                          {s.status === 'success' ? (
                            <Badge className="bg-green-100 text-green-700 text-[10px]">
                              {language === 'ar' ? 'ناجح' : 'Success'}
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 text-[10px]">
                              {language === 'ar' ? 'فشل' : 'Failed'}
                              {s.reason && (
                                <span className="ms-1 text-[9px] opacity-70">· {s.reason}</span>
                              )}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-[11px] text-[#655dc6]/80" data-testid={`session-city-${s.id}`}>
                          {s.city ? (
                            <span className="inline-flex items-center gap-1">
                              <MapPin size={11} weight="duotone" className="text-[#655dc6]/60 shrink-0" />
                              <span className="truncate max-w-[140px]" title={[s.city, s.region, s.country].filter(Boolean).join(' · ')}>
                                {s.city}
                                {s.country_code && <span className="ms-1 text-[9px] opacity-60">{s.country_code}</span>}
                              </span>
                            </span>
                          ) : (
                            <span className="text-[#655dc6]/30">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-[11px] tabular-nums text-[#655dc6]/70" dir="ltr">
                          {s.ip_address || '-'}
                        </TableCell>
                        <TableCell className="text-center text-[11px] text-[#655dc6]/70 max-w-[260px] truncate mx-auto" title={s.user_agent || ''} dir="ltr">
                          {s.user_agent || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLoginSessionsDialog(false)} data-testid="close-sessions-btn">
                {t('close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function InfoRow({ label, value }) {
  // `text-start` follows the document direction set on the dialog
  // wrapper (`dir={direction}`). In Arabic RTL this aligns labels and
  // values to the right edge of each grid cell — keeping the entire
  // detail panel visually consistent regardless of the language of the
  // value (Arabic or English).
  return (
    <div className="text-start">
      <p className="text-[#655dc6]/60 text-xs">{label}</p>
      <p className="text-[#655dc6] font-medium">{value || '-'}</p>
    </div>
  );
}


// ── Weekly schedule view (Saturday → Thursday) ──────────────────────
// Reads the resolved working hours from the employee record (department
// default OR per-employee custom_schedule with day_overrides) and renders
// a compact day-by-day grid. Friday and rest days are flagged as "off".
const WEEK_DAYS = [
  // weekday index in JS Date.getDay(): Sun=0, Mon=1, ..., Sat=6
  // We display Saturday first per local convention.
  { jsIdx: 6, label_ar: 'السبت',    label_en: 'Saturday'  },
  { jsIdx: 0, label_ar: 'الأحد',    label_en: 'Sunday'    },
  { jsIdx: 1, label_ar: 'الاثنين',  label_en: 'Monday'    },
  { jsIdx: 2, label_ar: 'الثلاثاء', label_en: 'Tuesday'   },
  { jsIdx: 3, label_ar: 'الأربعاء', label_en: 'Wednesday' },
  { jsIdx: 4, label_ar: 'الخميس',   label_en: 'Thursday'  },
  { jsIdx: 5, label_ar: 'الجمعة',   label_en: 'Friday'    },
];

// Backend uses Python's Monday=0..Sunday=6 indexing for `day_overrides`
// and `rest_days`. Convert from JS Date.getDay() Sun=0..Sat=6.
const jsDayToPy = (d) => (d + 6) % 7;

function _scheduleFor(employee) {
  // Prefer the server-resolved schedule (handles custom → department →
  // global defaults consistently). Fall back to the raw fields for
  // backwards compatibility with older cached responses.
  return employee?.effective_schedule
      || employee?.custom_schedule
      || employee?.department_schedule
      || {};
}

function EmployeeWeeklyScheduleView({ employee, language }) {
  const sched = _scheduleFor(employee);
  const isAr = language === 'ar';
  const overrides = sched.day_overrides || {};
  const restDays = Array.isArray(sched.rest_days) ? sched.rest_days : [5]; // default: Friday off
  // No schedule fields at all → show a clear notice instead of inventing
  // 08:00–16:00 for every day (the old behaviour that produced the
  // wrong-looking cards reported by HR).
  const hasAnyHours = !!(sched.start_time || sched.end_time || Object.keys(overrides).length > 0);
  const defaultStart = sched.start_time || null;
  const defaultEnd   = sched.end_time   || null;
  const defaultStart2 = sched.start_time2 || null;
  const defaultEnd2   = sched.end_time2   || null;

  // Doctors / contracted / flexible employees may have no fixed schedule.
  if (employee?.is_doctor) {
    return (
      <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
        {isAr ? 'طبيب — لا يوجد جدول حضور ثابت' : 'Doctor — no fixed schedule'}
      </div>
    );
  }
  if (employee?.is_contracted) {
    return (
      <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-800">
        {isAr ? 'عامل بعقد خارجي — جدول مرن' : 'Contracted worker — flexible schedule'}
      </div>
    );
  }

  if (!hasAnyHours) {
    return (
      <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-600">
        {isAr ? 'لم يُعدّ جدول للموظف بعد — يستخدم الإعدادات الافتراضية للنظام.' : 'No schedule configured — using system defaults.'}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
      {WEEK_DAYS.map((d) => {
        const pyIdx = jsDayToPy(d.jsIdx);
        const isRest = restDays.includes(pyIdx);
        const ov = overrides[String(pyIdx)] || {};
        // Resolution order: per-day override → base schedule → null.
        const start1 = ov.start_time || (isRest ? null : defaultStart);
        const end1   = ov.end_time   || (isRest ? null : defaultEnd);
        const start2 = ov.start_time2 || (isRest ? null : defaultStart2);
        const end2   = ov.end_time2   || (isRest ? null : defaultEnd2);
        const isFlex = ov.flexible_hours != null && Number(ov.flexible_hours) > 0;

        return (
          <div
            key={d.jsIdx}
            className={`rounded-lg p-2 border ${isRest ? 'bg-gray-50 border-gray-200' : 'bg-[#F4F2FB] border-[#C4BFF0]/40'}`}
            data-testid={`emp-schedule-day-${pyIdx}`}
          >
            <p className={`text-xs font-semibold ${isRest ? 'text-gray-500' : 'text-[#655dc6]'} mb-1`}>
              {isAr ? d.label_ar : d.label_en}
            </p>
            {isRest ? (
              <p className="text-xs text-gray-500">{isAr ? 'إجازة أسبوعية' : 'Rest day'}</p>
            ) : isFlex ? (
              <p className="text-xs font-mono text-[#655dc6]/80">
                {isAr ? `مرن — ${ov.flexible_hours} س` : `Flex — ${ov.flexible_hours}h`}
              </p>
            ) : (
              <div className="space-y-0.5">
                {start1 && end1 && (
                  <p className="text-xs font-mono text-[#655dc6]/80" dir="ltr">{start1} – {end1}</p>
                )}
                {start2 && end2 && (
                  <p className="text-xs font-mono text-[#655dc6]/80" dir="ltr">{start2} – {end2}</p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
